import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent } from "./ui/card";
import { fetchPortfolioTimeline } from "../lib/api";

const INR = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const inr = (n) => (n == null ? "—" : `₹${INR.format(Math.round(Number(n) || 0))}`);
const inrCompact = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(1)}Cr`;
  if (Math.abs(v) >= 1_00_000) return `₹${Math.round(v / 1_00_000)}L`;
  if (Math.abs(v) >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
  return `₹${Math.round(v)}`;
};
const shortMonth = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
};

/**
 * Compact portfolio-growth chart shown on the dashboard above Sum Assured. Renders nothing
 * until the member has at least ONE CAS snapshot — the empty state is the CTA to import.
 */
export function PortfolioGrowthCard() {
  const [timeline, setTimeline] = useState(null);
  useEffect(() => {
    fetchPortfolioTimeline().then(setTimeline).catch(() => setTimeline([]));
  }, []);

  if (timeline == null) return null; // loading — quiet

  if (timeline.length === 0) {
    return (
      <Card className="border-dashed border-gold/30" data-testid="portfolio-growth-empty">
        <CardContent className="flex flex-col items-start gap-2 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold">
              <TrendingUp className="h-3.5 w-3.5" />
              Portfolio gain
            </div>
            <p className="mt-1 max-w-md text-sm text-muted">
              Import your CAS to plot monthly portfolio value and see SIP + market P&amp;L
              side-by-side.
            </p>
          </div>
          <Link
            to="/app/net-worth"
            className="inline-flex items-center gap-1 text-sm font-medium text-gold hover:underline"
            data-testid="portfolio-growth-import-cta"
          >
            Import a CAS
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </CardContent>
      </Card>
    );
  }

  const first = timeline[0];
  const last = timeline[timeline.length - 1];
  const growth = (last?.total_value || 0) - (first?.total_value || 0);
  const growthPct = first?.total_value > 0 ? (growth / first.total_value) * 100 : 0;
  const invested = last?.cumulative_invested || 0;
  const marketPnl = growth - invested;
  const positive = growth >= 0;

  const chartData = timeline.map((s) => ({
    ...s,
    label: shortMonth(s.statement_to),
  }));

  // Zoom the Y-axis to just below the smallest value so small % moves are actually visible.
  // Without this, a 2% move on a ₹12L portfolio looks like a flat line because Recharts
  // includes 0 in the domain. Padding of 3% below and 5% above keeps a bit of breathing
  // room; if the range is degenerate (single snapshot / all equal), fall back to 'auto'.
  const values = chartData.map((r) => Number(r.total_value) || 0).filter((v) => v > 0);
  const yMin = values.length ? Math.min(...values) : 0;
  const yMax = values.length ? Math.max(...values) : 0;
  const range = yMax - yMin;
  const useZoomedAxis = range > 0 && yMin > range * 5; // only zoom when 0 is far below
  const yDomain = useZoomedAxis ? [yMin - range * 0.3, yMax + range * 0.15] : ["auto", "auto"];

  return (
    <Card data-testid="portfolio-growth-card">
      <CardContent className="p-4 sm:p-6">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold">
              <TrendingUp className="h-3.5 w-3.5" />
              Portfolio gain
            </div>
            <h3 className="mt-1 font-serif text-2xl text-text">
              {inr(last?.total_value)}
              <span
                className={`ml-3 text-sm ${positive ? "text-emerald-300" : "text-red-400"}`}
              >
                {positive ? "+" : ""}{inrCompact(growth)}
                {" "}({positive ? "+" : ""}{growthPct.toFixed(2)}%)
              </span>
            </h3>
            <p className="mt-1 text-xs text-muted">
              Invested {inrCompact(invested)} · Market P&amp;L{" "}
              <span className={marketPnl >= 0 ? "text-emerald-300" : "text-red-400"}>
                {marketPnl >= 0 ? "+" : ""}{inrCompact(marketPnl)}
              </span>
            </p>
          </div>
          <Link
            to="/app/portfolio/timeline"
            className="inline-flex items-center gap-1 text-sm font-medium text-gold hover:underline"
            data-testid="portfolio-growth-detail-cta"
          >
            Full timeline
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="h-40 relative">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="pg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F4C64B" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#F4C64B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="label" tick={{ fill: "#a5a29c", fontSize: 11 }} />
              <YAxis
                tick={{ fill: "#a5a29c", fontSize: 11 }}
                tickFormatter={inrCompact}
                domain={yDomain}
                allowDataOverflow={false}
              />
              <Tooltip
                contentStyle={{
                  background: "#1a1917",
                  border: "1px solid rgba(244,198,75,0.35)",
                  borderRadius: 6,
                  fontSize: 12,
                }}
                labelStyle={{ color: "#e4dfd0" }}
                formatter={(val) => [inr(val), "Value"]}
              />
              <Area
                type="monotone"
                dataKey="total_value"
                stroke="#F4C64B"
                strokeWidth={2}
                fill="url(#pg)"
                dot={{ r: 3, fill: "#F4C64B" }}
              />
            </AreaChart>
          </ResponsiveContainer>
          {useZoomedAxis && (
            <span
              className="absolute bottom-6 left-0 text-[10px] font-mono text-muted/60"
              title="Y-axis is zoomed to the data range — the ₹0 baseline is not shown"
              data-testid="portfolio-growth-axis-break"
            >
              ⌇
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
